import React, { useState } from "react";
import { TbAlertTriangle } from "react-icons/tb";
import { Button, Field, Modal, Alert } from "../../components";
import { TIERS, TIER_TONE, OPERATORS, isBetween, describeRule, checkBands, toPayload } from "../../lib/thresholds";
import DataSourcePicker, { serializeSources, parseSources } from "./DataSourcePicker";
import IconPicker, { CategoryIcon } from "./IconPicker";
import "./Settings.css";

// Creating and removing the scoring catalogue itself: the categories and the scan groups
// they sit in.
//
// This is a different kind of edit from re-banding, and the dialogs say so. Bands are per
// workspace — `tenant_category_thresholds` is keyed by tenant. Categories and scan groups
// are NOT: `report_item_categories` and `scan_groups` carry no tenant_id at all, so adding
// or deleting one changes the catalogue for every workspace on the installation. Two
// controls that look alike doing things at two different scopes is how someone deletes
// another tenant's scoring by accident.

const PLATFORM_NOTE = (
  <div className="v2-thr-warn" style={{ marginBottom: ".875rem" }}>
    <TbAlertTriangle size={15} />
    <div>
      <b>This affects every workspace</b>
      <div>Categories and scan groups are shared platform-wide, unlike the bands, which are yours alone.</div>
    </div>
  </div>
);

// A sensible three-band starting point. A category with no bands scores nothing, so
// creating one with empty rules would produce a row that silently never matches.
// The tints the seeded groups already use, so a new group looks like it belongs.
const PRESET_COLOURS = [
  "#3b82f6", "#0d9488", "#8b5cf6", "#f59e0b",
  "#ef4444", "#d946ef", "#6366f1", "#0ea5e9",
];

// bg and border are derived from the chosen colour as low-opacity forms of it. Hex with
// an alpha suffix, matching what the previous interface stored, so the two agree.
const tint = (color) => ({ bg: `${color}10`, border: `${color}33` });

const STARTER = {
  Good:     { operator: "<=", value: 20 },
  Moderate: { operator: "Between", lower: 21, upper: 50 },
  Bad:      { operator: ">", value: 50 },
};

export const NewCategoryModal = ({ groups, defaultGroup, onClose, onCreate }) => {
  const [form, setForm] = useState({
    name: "", scan_type: defaultGroup || "", metric: "", description: "", icon: "",
  });
  // The datasets the number is read from. A list, not a string: a category can chain
  // more than one, and the stored string is assembled from these on submit.
  const [sources, setSources] = useState(() => parseSources(""));
  const [rules, setRules] = useState(() => JSON.parse(JSON.stringify(STARTER)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setTier = (tier, patch) => setRules((r) => ({ ...r, [tier]: { ...r[tier], ...patch } }));
  const problems = checkBands(rules);

  const submit = async () => {
    if (!form.name.trim()) { setErr("Give the category a name."); return; }
    setBusy(true); setErr("");
    const ok = await onCreate({
      name: form.name.trim(),
      // Every threshold in this product is a whole number; the column exists but nothing
      // reads any other value, so it is set rather than asked for.
      value_type: "numeric",
      default_thresholds: toPayload(rules),
      scan_type: form.scan_type || null,
      metric: form.metric.trim() || null,
      description: form.description.trim() || null,
      data_source: serializeSources(sources) || null,
      // Carried into the report generator's threshold map, so it reaches the report,
      // not just this screen.
      icon: form.icon || null,
    });
    setBusy(false);
    if (ok) onClose(); else setErr("Could not create that category.");
  };

  return (
    <Modal title="New category" onClose={onClose} wide
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</Button>
      </>}>
      {PLATFORM_NOTE}
      <Alert>{err}</Alert>

      <div className="v2-grid2">
        <Field label="Name">
          <input className="v2-input" value={form.name} onChange={set("name")} autoFocus />
          {/* Not decoration: findingsGenerator matches a finding to its category by this
              exact string, so a name nothing produces creates a category that never scores. */}
          <p className="v2-ghl-help">Must match the finding name the scanner emits, exactly.</p>
        </Field>
        <Field label="Scan group">
          <select className="v2-input" value={form.scan_type} onChange={set("scan_type")}>
            <option value="">Uncategorised</option>
            {groups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </Field>
      </div>

      <div className="v2-grid2">
        <Field label="Measures"
               hint="The short label shown in the Measures column of the table.">
          <input className="v2-input" value={form.metric} onChange={set("metric")}
                 placeholder="Devices without recent backups" />
        </Field>
        <IconPicker value={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} />
      </div>

      <Field label="Description"
             hint="A fuller sentence, shown when the category is opened. Not used for scoring.">
        <input className="v2-input" value={form.description} onChange={set("description")}
               placeholder="Percentage of devices without a recent backup." />
      </Field>
      <DataSourcePicker rows={sources} onChange={setSources} />

      <div className="v2-section-title" style={{ marginTop: ".5rem" }}>Default bands</div>
      {TIERS.map((tier) => {
        const rule = rules[tier];
        const between = isBetween(rule);
        return (
          <div key={tier} className="v2-thr-tier">
            <div className="v2-thr-tier-head">
              <span className={`v2-rbadge v2-rbadge--${TIER_TONE[tier]}`}>{tier.toUpperCase()}</span>
              <span className="v2-thr-preview">{describeRule(rule)}</span>
            </div>
            <div className="v2-thr-controls">
              <Field label="Condition">
                <select className="v2-input" value={rule.operator || ""}
                        aria-label={`${tier} operator`}
                        onChange={(e) => setTier(tier, { operator: e.target.value })}>
                  <option value="">Not set</option>
                  {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {between ? (
                <>
                  <Field label="From">
                    <input className="v2-input" type="number" value={rule.lower ?? ""}
                           aria-label={`${tier} lower bound`}
                           onChange={(e) => setTier(tier, { lower: e.target.value })} />
                  </Field>
                  <Field label="To">
                    <input className="v2-input" type="number" value={rule.upper ?? ""}
                           aria-label={`${tier} upper bound`}
                           onChange={(e) => setTier(tier, { upper: e.target.value })} />
                  </Field>
                </>
              ) : (
                <Field label="Value">
                  <input className="v2-input" type="number" value={rule.value ?? ""}
                         aria-label={`${tier} value`} disabled={!rule.operator}
                         onChange={(e) => setTier(tier, { value: e.target.value })} />
                </Field>
              )}
            </div>
          </div>
        );
      })}

      {problems.length > 0 && (
        <div className="v2-thr-warn">
          <TbAlertTriangle size={15} />
          <div>
            <b>Check these bands</b>
            <ul>{problems.map((p, i) => <li key={i}>{p.text}</li>)}</ul>
          </div>
        </div>
      )}
    </Modal>
  );
};

// Create and rename in one dialog. `key` is what every category's scan_type points at, so
// it is set once and never editable afterwards — changing it would orphan every category
// in the group without touching them.
export const GroupModal = ({ group, onClose, onSave }) => {
  const editing = !!group;
  const [form, setForm] = useState({
    key: group?.key || "",
    label: group?.label || "",
    color: group?.color || "#64748b",
    icon: group?.icon || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.label.trim()) { setErr("Give the group a name."); return; }
    if (!editing && !form.key.trim()) { setErr("Give the group a key."); return; }
    setBusy(true); setErr("");
    const ok = await onSave({
      key: form.key.trim(),
      label: form.label.trim(),
      color: form.color,
      icon: form.icon || null,
      // Sent whole because the update has no COALESCE — anything omitted is written null.
      // bg and border are derived from the chosen colour rather than asked for: three
      // separate colour wells for one theme is three ways to make it look wrong.
      ...tint(form.color),
    });
    setBusy(false);
    if (ok) onClose(); else setErr("Could not save that group.");
  };

  return (
    <Modal title={editing ? `Edit ${group.label}` : "New scan group"} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </>}>
      {PLATFORM_NOTE}
      <Alert>{err}</Alert>

      <Field label="Name">
        <input className="v2-input" value={form.label} onChange={set("label")} autoFocus />
      </Field>
      <Field label="Key"
             hint={editing
               ? "Fixed. Every category in this group points at it by name."
               : "The identifier categories are filed under. Lower case, no spaces."}>
        <input className="v2-input v2-mono-input" value={form.key} onChange={set("key")}
               disabled={editing} placeholder="Network Scan" />
      </Field>
      <div className="v2-grid2">
        <Field label="Theme colour" hint="Tints this group's heading and its badges.">
          <div className="v2-swatchrow">
            <input className="v2-input v2-colorinput" type="color" value={form.color}
                   aria-label="Theme colour" onChange={set("color")} />
            <div className="v2-groupswatches">
              {PRESET_COLOURS.map((c) => (
                <button key={c} type="button" title={c} aria-label={`Use ${c}`}
                        className={`v2-groupswatch${form.color.toLowerCase() === c ? " on" : ""}`}
                        style={{ background: c }}
                        onClick={() => setForm((f) => ({ ...f, color: c }))} />
              ))}
            </div>
          </div>
        </Field>
        <IconPicker value={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} />
      </div>

      {/* The theme as it will actually read, rather than a colour well on its own. */}
      <div className="v2-grouppreview" style={{ borderColor: tint(form.color).border, background: tint(form.color).bg }}>
        <span className="v2-grouppreview-mark" style={{ background: form.color }}>
          <CategoryIcon name={form.icon} size={14} />
        </span>
        <span style={{ color: form.color, fontWeight: 600 }}>{form.label || "Group name"}</span>
      </div>
      {editing && group.is_system === 1 && (
        <p className="v2-ghl-help">
          This is a system group. Its name and colour can change; it cannot be deleted.
        </p>
      )}
    </Modal>
  );
};

// One confirm dialog for both deletes, because the thing worth getting right is the same
// in both cases: saying plainly what disappears and what does not.
export const DeleteConfirm = ({ title, body, danger, confirmLabel, onClose, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const ok = await onConfirm();
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal title={title} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={go} disabled={busy || danger}>
          {busy ? "Deleting…" : confirmLabel}
        </Button>
      </>}>
      {body}
    </Modal>
  );
};
